import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Stack,
  Popover,
  TextField,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ImageIcon from '@mui/icons-material/Image';
import ArchiveIcon from '@mui/icons-material/Archive';
import PrintIcon from '@mui/icons-material/Print';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchOffIcon from '@mui/icons-material/SearchOff';

const ItemCard = ({ item, startEditing, markAsSold, markAsMissing, deleteItem, checkItemTransactions, archiveItem, channelsMap = {}, onPrintLabel, labelRequiredChannelIds = new Set(), onRequestBarcodes, isRequestingBarcodes = false, quailhqChannelIds = new Set() }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [quantitySold, setQuantitySold] = useState('');
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [pendingDeleteAnchorEl, setPendingDeleteAnchorEl] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [hasTransactions, setHasTransactions] = useState(false);
  const [transactionsCount, setTransactionsCount] = useState(0);
  const [isCheckingTransactions, setIsCheckingTransactions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const isSold = item.isSold;
  const isMissing = item.isMissing ?? false;
  const statusColor = isMissing ? 'error' : isSold ? 'success' : 'primary';
  const statusLabel = isMissing ? 'Missing' : isSold ? 'Sold' : 'Available';

  const handleDeleteClick = async (event) => {
    const anchorEl = event.currentTarget;
    setPendingDeleteAnchorEl(anchorEl);
    setDeleteConfirmText('');
    setHasTransactions(false);
    setTransactionsCount(0);
    setIsCheckingTransactions(true);
    try {
      if (!checkItemTransactions) {
      }
      const result = checkItemTransactions
        ? await checkItemTransactions(item.id)
        : { hasTransactions: false, count: 0 };
      setHasTransactions(!!result?.hasTransactions);
      setTransactionsCount(result?.count ?? 0);
    } finally {
      setIsCheckingTransactions(false);
      setDeleteAnchorEl(anchorEl);
      setPendingDeleteAnchorEl(null);
    }
  };

  const handleDeleteConfirmClose = () => {
    setDeleteAnchorEl(null);
    setPendingDeleteAnchorEl(null);
    setDeleteConfirmText('');
    setHasTransactions(false);
    setTransactionsCount(0);
    setIsCheckingTransactions(false);
    setIsDeleting(false);
  };

  const handleDeleteConfirmed = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    if (hasTransactions) {
      if (deleteConfirmText === 'yes') {
        const deleted = await deleteItem(item.id);
        if (deleted) {
          handleDeleteConfirmClose();
        } else {
          setIsDeleting(false);
        }
      } else {
        setIsDeleting(false);
      }
    } else {
      const deleted = await deleteItem(item.id);
      if (deleted) {
        handleDeleteConfirmClose();
      } else {
        setIsDeleting(false);
      }
    }
  };

  const isDeleteEnabled = hasTransactions ? deleteConfirmText === 'yes' : true;

  const handleMarkAsSoldClick = (event) => {
    const itemQty = item.itemQuantity ? parseInt(item.itemQuantity, 10) : 0;
    
    if (itemQty === 1) {
      // Directly mark as sold with quantity 1
      markAsSold({ ...item, quantitySold: 1 });
    } else if (itemQty > 1) {
      // Show popover to enter quantity sold
      setAnchorEl(event.currentTarget);
      setQuantitySold('');
    } else {
      // No quantity available
      setValidationError('Cannot mark as sold: No quantity available');
      return;
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setQuantitySold('');
  };

  const handleConfirmSale = () => {
    const qtySold = parseInt(quantitySold, 10);
    const itemQty = parseInt(item.itemQuantity, 10);
    
    if (isNaN(qtySold) || qtySold <= 0) {
      setValidationError('Please enter a valid quantity');
      return;
    }
    
    if (qtySold > itemQty) {
      setValidationError(`Cannot sell ${qtySold}. Only ${itemQty} available.`);
      return;
    }
    
    markAsSold({ ...item, quantitySold: qtySold });
    handlePopoverClose();
  };

  const open = Boolean(anchorEl);

  return (
    <Card
      key={item.id}
      sx={{
        flex: 1,
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: 520,
        overflow: 'hidden',
        opacity: isSold ? 0.7 : 1,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      {/* Main Image */}
      {item.mainImageUrl ? (
        <CardMedia
          component="img"
          image={item.mainImageUrl}
          alt={item.description || item.itemTitle || 'Item image'}
          sx={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            backgroundColor: '#f5f5f5',
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 200,
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ImageIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, overflow: 'hidden', pr: 1.5, minWidth: 0 }}>
        <Stack spacing={2} sx={{ width: '100%', minWidth: 0 }}>
          {/* Title and Status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
                minWidth: 0,
              }}
            >
              {item.itemTitle}
            </Typography>
            <Chip
              icon={isSold ? <CheckCircleIcon /> : undefined}
              label={statusLabel}
              color={statusColor}
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Box>

          {/* Item Number */}
          {item.itemNumber && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
              Item #: {item.itemNumber}
            </Typography>
          )}

          {/* Description */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              height: '3em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {item.description || '\u00A0'}
          </Typography>

          {/* Details Grid */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
            <Box sx={{ flex: '0 1 calc(50% - 4px)' }}>
              <Typography variant="caption" color="textSecondary">
                List Price
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                ${parseFloat(item.listPrice).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: '0 1 calc(50% - 4px)' }}>
              <Typography variant="caption" color="textSecondary">
                Cost
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                ${parseFloat(item.cost).toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: '0 1 calc(50% - 4px)' }}>
              <Typography variant="caption" color="textSecondary">
                Sales Channel
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {quailhqChannelIds.has(item.salesChannelId) && (
                  <Box component="img" src="/assets/quail-hq-logo.png" alt="QuailHQ" sx={{ height: 16, width: 'auto' }} />
                )}
                <Typography variant="body2">
                  {channelsMap[item.salesChannelId] || 'Unassigned'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flex: '0 1 calc(50% - 4px)' }}>
              <Typography variant="caption" color="textSecondary">
                Location
              </Typography>
              <Typography variant="body2" noWrap>
                {item.salesLocation || ''}
              </Typography>
            </Box>
            <Box sx={{ flex: '0 1 calc(50% - 4px)' }}>
              <Typography variant="caption" color="textSecondary">
                Quantity
              </Typography>
              <Typography variant="body2">
                {item.itemQuantity ?? 0}
              </Typography>
            </Box>
            {item.itemLink && (
              <Box sx={{ flex: '1 1 100%' }}>
                <Typography variant="caption" color="textSecondary">
                  Listing
                </Typography>
                {item.itemLink.startsWith('http://') || item.itemLink.startsWith('https://') ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      component="a"
                      href={item.itemLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: { xs: 150, sm: 180 },
                        display: 'block',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {item.itemLink}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                  </Box>
                ) : (
                  <Typography variant="body2" noWrap>
                    {item.itemLink}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>

      {/* Validation Error */}
      {validationError && (
        <Alert severity="warning" onClose={() => setValidationError('')} sx={{ mb: 1, mx: 2 }}>{validationError}</Alert>
      )}

      {/* Actions */}
      <CardActions
        sx={{
          display: 'flex',
          gap: 1,
          padding: 2,
          paddingTop: 0,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          minHeight: '44px',
        }}
      >
        {item.itemNumber && onPrintLabel && labelRequiredChannelIds.has(item.salesChannelId) && (
          <Tooltip title="Print Label">
            <IconButton size="small" onClick={() => onPrintLabel(item)} aria-label="Print Label">
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onRequestBarcodes && quailhqChannelIds.has(item.salesChannelId) && (
          <Tooltip title="Request QuailHQ Barcode">
            <span>
              <IconButton size="small" onClick={() => onRequestBarcodes([item.id])} disabled={isRequestingBarcodes} aria-label="Request QuailHQ Barcode">
                <QrCode2Icon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => startEditing(item)} aria-label="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!isSold && (
          parseInt(item.itemQuantity, 10) > 0 ? (
            <Tooltip title="Sell">
              <IconButton
                size="small"
                color="success"
                onClick={handleMarkAsSoldClick}
                disabled={isMissing}
                aria-label="Sell"
              >
                <AttachMoneyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Sold">
              <span>
                <IconButton size="small" color="success" disabled aria-label="Sold">
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        )}
        {isSold && parseInt(item.itemQuantity, 10) === 0 && archiveItem && (
          <Tooltip title="Archive">
            <IconButton size="small" color="secondary" onClick={() => archiveItem(item)} aria-label="Archive">
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isSold && markAsMissing && (
          <Tooltip title={isMissing ? 'Mark as Found' : 'Mark as Missing'}>
            <IconButton
              size="small"
              color={isMissing ? 'warning' : 'default'}
              onClick={() => markAsMissing(item)}
              aria-label={isMissing ? 'Mark as Found' : 'Mark as Missing'}
            >
              <SearchOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={handleDeleteClick}
              disabled={isCheckingTransactions || !!pendingDeleteAnchorEl}
              aria-label="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </CardActions>

      {/* Quantity Sold Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Enter Quantity Sold
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
            Available: {item.itemQuantity}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Quantity Sold"
            value={quantitySold}
            onChange={(e) => setQuantitySold(e.target.value)}
            inputProps={{ min: 1, max: item.itemQuantity }}
            size="small"
            sx={{ mb: 2 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmSale();
              }
            }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handlePopoverClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleConfirmSale}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteConfirmClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 3, minWidth: 350, maxWidth: 400 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {isCheckingTransactions
              ? 'Checking for related transactions...'
              : hasTransactions
                ? `This item has ${transactionsCount} related sales transaction${transactionsCount === 1 ? '' : 's'}. Deleting the item will delete these transactions and affect your sales reporting. Are you sure you want to delete this item and the related transaction(s)?`
                : 'Deleting an item is irreversible. The item and all associated data & images will be deleted. Are you sure?'}
          </Typography>
          {hasTransactions && (
            <TextField
              fullWidth
              label="Type 'yes' to delete the item and related transaction(s)"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              placeholder="yes"
            />
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleDeleteConfirmClose}>
              {isSold ? 'No, go back' : 'Go Back'}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              disabled={isCheckingTransactions || isDeleting || !isDeleteEnabled}
            >
              {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes, Delete'}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Card>
  );
};

export default ItemCard;
